/**
 * Campaign ENROLLMENT aggregation for the Leader "Campaigns" tab — the per-person "one-time work"
 * (enablement) figure, the enrollment peer of the delivery rollup in `campaignLeader.ts`.
 *
 * A `CampaignDelivery` is customer-facing work an SE runs (bucketed by `owner`); a `CampaignEnrollment`
 * is that SE's own once-per-campaign enablement run, and its server-computed `progress.percent` is the
 * natural "how far through the one-time work" signal. Deliveries are keyed by `owner` email, but an
 * enrollment is keyed by `user` (id) with `user_email` optional, so we resolve each enrollment to a
 * lowercased email — preferring `user_email`, else mapping the `user` id through the org rows (which
 * carry both `user_id` and `user_email`) — so it joins the org tree exactly like everything else here.
 *
 * Pure module — no React, no fetching. Callers pass the already-fetched enrollments and org rows.
 */
import {
  computeSubtreeScope,
  getAllReportsEmails,
  getDirectReports,
  isManagerEmail,
  type OrgChartRow,
} from './orgScope';
import { enrollmentPercent } from './campaignAnalytics';
import { enrollmentStatus } from './campaigns';
import type { CampaignEnrollment, EnrollmentStatus } from './campaigns';
import type { CompletionCohort } from './campaignSalesLevels';

/** Per-person enablement figure: how many enrollments, split by status, plus average completion. */
export interface EnrollmentMetrics {
  total: number;
  byStatus: Record<EnrollmentStatus, number>;
  /** How many enrollments are fully completed — the "one-time work done" count. */
  completed: number;
  /** Mean completion percent across the enrollments (0 when there are none). */
  avgPercent: number;
}

const ENROLLMENT_STATUSES: EnrollmentStatus[] = ['not_started', 'in_progress', 'completed'];

export function emptyEnrollmentMetrics(): EnrollmentMetrics {
  const byStatus = {} as Record<EnrollmentStatus, number>;
  for (const s of ENROLLMENT_STATUSES) byStatus[s] = 0;
  return { total: 0, byStatus, completed: 0, avgPercent: 0 };
}

/** Sum a set of enrollments into one figure. */
export function sumEnrollments(enrollments: CampaignEnrollment[]): EnrollmentMetrics {
  const metrics = emptyEnrollmentMetrics();
  let pctSum = 0;
  for (const e of enrollments) {
    metrics.total += 1;
    // Derived, composite-aware rollup — not the stale stored `status` column.
    const st = enrollmentStatus(e);
    if (st in metrics.byStatus) metrics.byStatus[st] += 1;
    if (st === 'completed') metrics.completed += 1;
    pctSum += enrollmentPercent(e);
  }
  metrics.avgPercent = metrics.total ? Math.round(pctSum / metrics.total) : 0;
  return metrics;
}

/**
 * Bucket every enrollment by the person it belongs to, keyed by lowercased email so it matches the org
 * rows the same way `deliveriesByOwner` does. Enrollments we cannot attribute to an email (no
 * `user_email` and no matching org row for the `user` id) are dropped.
 */
export function enrollmentsByUser(
  enrollments: CampaignEnrollment[],
  orgRows: OrgChartRow[],
): Map<string, CampaignEnrollment[]> {
  const emailByUserId = new Map<number, string>();
  for (const row of orgRows) {
    if (row.user_id != null && row.user_email) {
      emailByUserId.set(row.user_id, String(row.user_email).toLowerCase());
    }
  }
  const byUser = new Map<string, CampaignEnrollment[]>();
  for (const e of enrollments) {
    const email = (e.user_email ?? emailByUserId.get(e.user) ?? '').trim().toLowerCase();
    if (!email) continue;
    const list = byUser.get(email) ?? [];
    list.push(e);
    byUser.set(email, list);
  }
  return byUser;
}

/**
 * The enrollments attributable to `email` rolled up over the org subtree it roots — the person's own
 * plus every descendant's. For an individual contributor (a leaf) this is just their own.
 */
export function subtreeEnrollments(
  email: string,
  orgRows: OrgChartRow[],
  byUser: Map<string, CampaignEnrollment[]>,
): CampaignEnrollment[] {
  const scope = computeSubtreeScope(orgRows, email);
  const seen = new Set<number>();
  const out: CampaignEnrollment[] = [];
  for (const scopedEmail of scope.emails) {
    const list = byUser.get(scopedEmail.toLowerCase());
    if (!list) continue;
    for (const e of list) {
      if (seen.has(e.id)) continue;
      seen.add(e.id);
      out.push(e);
    }
  }
  return out;
}

/** The subtree enablement figure for `email` (own + all reports). */
export function subtreeEnrollmentMetrics(
  email: string,
  orgRows: OrgChartRow[],
  byUser: Map<string, CampaignEnrollment[]>,
): EnrollmentMetrics {
  return sumEnrollments(subtreeEnrollments(email, orgRows, byUser));
}

// ---------------------------------------------------------------------------
// Server-cohort path (scalable): the same subtree rollup, but from the pre-aggregated per-owner cohorts
// the `db/campaign/{pk}/completion?by=owner&manager=<email>` endpoint returns instead of raw enrollment
// rows. The endpoint already scopes to the manager's subtree server-side; these helpers re-roll it per
// direct report (each report's own sub-subtree) using the org tree, so the Leader page never fetches the
// (page-capped) enrollment table. Bucket-for-bucket identical to the row path above.
// ---------------------------------------------------------------------------

/** Index the per-owner cohorts by lowercased email (one cohort per person). */
export function cohortsByEmail(cohorts: CompletionCohort[]): Map<string, CompletionCohort> {
  const byEmail = new Map<string, CompletionCohort>();
  for (const c of cohorts || []) {
    const email = String(c.email ?? '').trim().toLowerCase();
    if (email) byEmail.set(email, c);
  }
  return byEmail;
}

/** Sum a set of per-owner cohorts into one enablement figure (avg = Σpercent_sum / Σenrolled). */
export function metricsFromCohorts(cohorts: CompletionCohort[]): EnrollmentMetrics {
  const metrics = emptyEnrollmentMetrics();
  let pctSum = 0;
  for (const c of cohorts) {
    metrics.total += c.enrolled;
    metrics.byStatus.not_started += c.not_started;
    metrics.byStatus.in_progress += c.in_progress;
    metrics.byStatus.completed += c.completed;
    metrics.completed += c.completed;
    pctSum += c.percent_sum;
  }
  metrics.avgPercent = metrics.total ? Math.round(pctSum / metrics.total) : 0;
  return metrics;
}

/** The per-owner cohorts falling within `email`'s org subtree (own + all reports). */
export function subtreeCohorts(
  email: string,
  orgRows: OrgChartRow[],
  byEmail: Map<string, CompletionCohort>,
): CompletionCohort[] {
  const scope = computeSubtreeScope(orgRows, email);
  const out: CompletionCohort[] = [];
  for (const scopedEmail of scope.emails) {
    const c = byEmail.get(scopedEmail.toLowerCase());
    if (c) out.push(c);
  }
  return out;
}

/** The subtree enablement figure for `email` from server cohorts — peer of {@link subtreeEnrollmentMetrics}. */
export function subtreeEnrollmentMetricsFromCohorts(
  email: string,
  orgRows: OrgChartRow[],
  byEmail: Map<string, CompletionCohort>,
): EnrollmentMetrics {
  return metricsFromCohorts(subtreeCohorts(email, orgRows, byEmail));
}

// ---------------------------------------------------------------------------
// Per-row "enablement completion" for the Leader per-team-member table.
//
// The figure reads differently by role, so the two rings of the org tree get two units:
//   • An individual contributor (a non-manager leaf) shows "journey maps complete of assigned" — their
//     own enrollment's server rollup (progress.counts: completed of journey_maps). `completed` folds
//     assessment completion in, so it is clamped to the journey-map count; when the raw enrollment isn't
//     loaded (cohort mode carries no per-map counts) it degrades to the cohort's completed/enrolled.
//   • A manager (or manager-of-managers) shows "users complete of users", where the unit is every
//     individual contributor ANYWHERE in the subtree — sub-managers are descended THROUGH, never counted
//     as a unit themselves.
// ---------------------------------------------------------------------------

/** A per-row enablement cell: `kind` picks the unit (journey maps for an IC, users for a manager). */
export interface EnablementCell {
  kind: 'journey_maps' | 'users';
  done: number;
  total: number;
}

/** Whether one person finished their campaign enablement — every enrollment they hold is complete. */
export function personEnablementCompleted(
  email: string,
  byUser: Map<string, CampaignEnrollment[]>,
  byEmail: Map<string, CompletionCohort>,
): boolean {
  const key = String(email || '').trim().toLowerCase();
  const enrolls = byUser.get(key);
  if (enrolls && enrolls.length) return enrolls.every((e) => enrollmentStatus(e) === 'completed');
  const co = byEmail.get(key);
  if (co) return co.enrolled > 0 && co.completed >= co.enrolled;
  return false;
}

/** An IC's "journey maps complete of assigned" — raw rollup preferred, cohort completed/enrolled fallback. */
export function icJourneyMaps(
  email: string,
  byUser: Map<string, CampaignEnrollment[]>,
  byEmail: Map<string, CompletionCohort>,
): EnablementCell {
  const key = String(email || '').trim().toLowerCase();
  const enrolls = byUser.get(key);
  if (enrolls && enrolls.length) {
    let done = 0;
    let total = 0;
    for (const e of enrolls) {
      const c = e.progress?.counts;
      if (!c) continue;
      total += c.journey_maps;
      // `completed` counts journey maps AND assessments; clamp so an IC never reads "5 of 3 maps".
      done += Math.min(c.completed, c.journey_maps);
    }
    return { kind: 'journey_maps', done, total };
  }
  const co = byEmail.get(key);
  if (co) return { kind: 'journey_maps', done: co.completed, total: co.enrolled };
  return { kind: 'journey_maps', done: 0, total: 0 };
}

/**
 * A manager's "users complete of users" over every IC (non-manager) in the subtree, plus the IC emails
 * still outstanding — the reminder card's audience. Sub-managers are descended through, never a unit.
 */
export function managerUsersCompleted(
  email: string,
  orgRows: OrgChartRow[],
  byUser: Map<string, CampaignEnrollment[]>,
  byEmail: Map<string, CompletionCohort>,
): { cell: EnablementCell; incompleteEmails: string[] } {
  const icEmails = getAllReportsEmails(orgRows, email).filter((e) => !isManagerEmail(orgRows, e));
  let done = 0;
  const incompleteEmails: string[] = [];
  for (const ic of icEmails) {
    if (personEnablementCompleted(ic, byUser, byEmail)) done += 1;
    else incompleteEmails.push(ic);
  }
  return { cell: { kind: 'users', done, total: icEmails.length }, incompleteEmails };
}

/** One of a manager's direct reports and their own enablement figure. */
export interface ReportEnablement {
  email: string;
  isManager: boolean;
  /** `users` for a sub-manager (their team), `journey_maps` for an IC. */
  cell: EnablementCell;
}

/** Whether a report's own enablement is unfinished (their team, or their maps, still has work left). */
export function reportIsIncomplete(r: ReportEnablement): boolean {
  return r.cell.total > 0 && r.cell.done < r.cell.total;
}

/**
 * Each of a manager's DIRECT reports with a role-appropriate enablement figure — a sub-manager gets their
 * team's "users complete of users", an IC gets their "journey maps complete of assigned". This is the
 * per-report breakdown the reminder card lists (distinct from the row's own rolled-up column figure).
 */
export function directReportEnablement(
  managerEmail: string,
  orgRows: OrgChartRow[],
  byUser: Map<string, CampaignEnrollment[]>,
  byEmail: Map<string, CompletionCohort>,
): ReportEnablement[] {
  return getDirectReports(orgRows, managerEmail).map((r) => {
    const email = String(r.user_email || '').toLowerCase();
    const isManager = !!r.is_manager;
    const cell = isManager
      ? managerUsersCompleted(email, orgRows, byUser, byEmail).cell
      : icJourneyMaps(email, byUser, byEmail);
    return { email, isManager, cell };
  });
}
